import React, { useEffect, useState } from 'react'
import { Container } from '@radix-ui/themes'

import Reservation from '../interfaces/Reservation'
import Vehicle from '../interfaces/Vehicle'
import useReservationFetcher from '../hooks/useReservationFetcher'
import useVehicleFetcher from '../hooks/useVehicleFetcher'
import useClientFetcher from '../hooks/useClientFetcher'
import Client from '../interfaces/Client'
import { MessageCrea } from '../utils/utils'
import { AppPath } from '../enums/AppPath'
import { NvvAction, ReservaAction } from '../enums/DocActions'
import { useAuth } from '../hooks/useAuth'
import useReservationVoid from '../hooks/useReservationVoid'
import useReservationArchive from '../hooks/useReservationArchive'

import { ReservationNvvEdit } from './ReservationNvvEdit'
import { Alert, Info } from './MessageInfo'

const ReservationNvvDetailPage: React.FC<{ id: string; nvvInd?: number }> = ({
  id,
  nvvInd = NvvAction.SoloReserva,
}) => {
  const [reservation, setReservation] = useState<Reservation | null>(null)
  const { errorReservation, fetchReservation } = useReservationFetcher()
  const { errorVehicle, fetchVehicle } = useVehicleFetcher()
  const [vehicle, setVehicle] = useState<Vehicle>()
  const { errorClient, fetchClient } = useClientFetcher()
  const [client, setClient] = useState<Client>()
  const [infoMessage, setInfoMessage] = useState<string>('')
  const [errorMessage, setErrorMessage] = useState<string>('')
  const { userData } = useAuth()
  const { errorReservationVoid, reservationVoid } = useReservationVoid()
  const { errorReservationArchive, reservationArchive } = useReservationArchive()
  const [refreshKey, setRefreshKey] = useState(0) // A key to trigger re-fetching

  useEffect(() => {
    fetchDataAsync()
  }, [id, refreshKey])

  const fetchDataAsync = async () => {
    if (!id || id === '') {
      return
    }

    const newreservation = await fetchReservation(id)
    const doc = 'reserva'

    if (errorReservation) {
      setErrorMessage(errorReservation.message)

      return
    }

    if (!newreservation) {
      setErrorMessage(`Error recuperando la ${doc}`)

      return
    }

    const vehicleId = newreservation.vehicle
    const vehicle = await fetchVehicle(vehicleId)

    if (errorVehicle) {
      setErrorMessage(errorVehicle.message)

      return
    }

    if (!vehicle) {
      setErrorMessage('Error recuperando el vehículo')

      return
    }
    const clientId = newreservation.client

    const client = await fetchClient(clientId)

    if (errorClient) {
      setErrorMessage(errorClient.message)

      return
    }

    if (!client) {
      setErrorMessage('Error recuperando el cliente')

      return
    }
    setClient(client)
    setVehicle(vehicle)

    setReservation(newreservation)
  }

  const alertMessageTimeout = (): void => {
    setErrorMessage('')
  }

  const infoMessageTimeout = (): void => {
    setInfoMessage('')
  }

  const handleAnular = async (motivo: string) => {
    if (!reservation || !userData) return
    const id = reservation._id
    const by = userData.profile?._id ?? ''
    const res = await reservationVoid(id, motivo, by)

    if (res === null) {
      return
    }

    const msg = MessageCrea(AppPath.Reservations, ReservaAction.Anular)

    setInfoMessage(msg)
    handleRefresh()
  }

  const handleArchivar = async () => {
    if (!reservation || !userData) return
    const id = reservation._id
    const by = userData.profile?._id ?? ''
    const res = await reservationArchive(id, by)

    if (res === null) {
      return
    }

    const msg = MessageCrea(AppPath.Reservations, ReservaAction.Archivar)

    setInfoMessage(msg)
    handleRefresh()
  }

  const handleRefresh = () => setRefreshKey((prev) => prev + 1)

  return (
    <Container size="3">
      {errorMessage !== '' && <Alert message={errorMessage} trigger={Date.now()} onTimeout={alertMessageTimeout} />}
      {infoMessage !== '' && <Info message={infoMessage} trigger={Date.now()} onTimeout={infoMessageTimeout} />}

      {reservation && vehicle && client && (
        <>
          <ReservationNvvEdit
            vehicle={vehicle}
            client={client}
            reservation={reservation}
            nvvInd={nvvInd}
            handleAnular={handleAnular}
            handleArchivar={handleArchivar}
            onRefresh={handleRefresh}
          />
        </>
      )}

      <div>{errorReservationVoid?.message}</div>
      <div>{errorReservationArchive?.message}</div>
    </Container>
  )
}

export default ReservationNvvDetailPage
